import { Button, ColumnLayout, FormField, Icon, Input, Modal, Toggle, Select } from 'AWS-UI-Components-React';
import Box from "@amzn/awsui-components-react-v3/polaris/box";
import { Multiselect } from '@amzn/awsui-components-react-v3';
import React from 'react';
import { PHYSICAL_CONSUMER_PRODUCT_LINES } from '../../constants/product_line';
import { REPORT_TYPE } from '../../constants/report_type';
import { AccuracyButtonNewStyle, NegativeAccuracyButtonNewStyle, NegativeMarginAccuracyButtonNewStyle } from '../../styles/common_styles';
import { deepClone } from '../../utilities';
import { isAdmin } from '../../utils/access-utils';
import logger from '../../utils/logging';
import { ProductLinesButton } from '../buttons/product_lines_button';
import { TimeFrameButton } from '../buttons/time_frame_button';
import { getJSClient } from '../js_client';
import { DATE_YYYYMM, padZero, diffYYYYMM } from '../../utils/date-utils';
import { BRIDGING_UNAGGREGATED_LIMIT } from '../reports/bridging/bridging_constants';
import { BENCHMARK_ADJUSTMENT_COMPONENTS } from '../../constants/benchmark_adjustment_components';

export class NewSimulationWindow extends React.Component {
  constructor(props) {
    super(props);
    this.defaults = {
      loadCpData: '1',
      loadFortuneData: '3',
      loadDucsiData: '1',
      loadLiquidationData: '1',
      loadSpotData: '1',
      loadVariableFcBenchmarkData: '1',
    };
    this.state = {
      validated: false,
      errorText: '',
      componentOptions: props.components || [],
      productLinesOptions: props.productLines || [],
      params: {
        endToEndSimulation: false,
        hcpSimulationOutputPath: '',
        components: [],
        productLines: [],
        loadCpData: false,
        loadCpDataMonths: '0',
        loadFortuneData: false,
        loadFortuneDataMonths: '0',
        loadDucsiData: false,
        loadDucsiDataMonths: '0',
        loadLiquidationData: false,
        loadLiquidationDataMonths: '0',
        loadSpotData: false,
        loadSpotDataMonths: '0',
        loadVariableFcBenchmarkData: false,
        loadVariableFcBenchmarkDataMonths: '0',
        enableBenchmarkAdjustment: false,
        autoBenchmarkAdjustmentSourceLoadMonths: '0',
        startMonth: `${parseInt(DATE_YYYYMM.START_MONTH / DATE_YYYYMM.DIVIDEND)}/${padZero(
          DATE_YYYYMM.START_MONTH % DATE_YYYYMM.DIVIDEND
        )}`,
        endMonth: `${parseInt(DATE_YYYYMM.PREV_MONTH / DATE_YYYYMM.DIVIDEND)}/${padZero(
          DATE_YYYYMM.PREV_MONTH % DATE_YYYYMM.DIVIDEND
        )}`,
        syncRawData: false,
        syncAurora: true,
        runAccuracyPipeline: true,
        validationTestRunMonths: '3',
        skipValidationTests: true,
        publicView: false,
      },
      runningSimulation: false,
    };
    this.defaultParams = deepClone(this.state.params);
    this.client = getJSClient();
    this.monthsCoverage = diffYYYYMM(DATE_YYYYMM.START_MONTH, DATE_YYYYMM.PREV_MONTH) + 1;
  }

  validate = () => {
    let params = this.state.params;

    if (!(params.name && params.startMonth && params.endMonth)) {
      this.setState({ validated: false, errorText: 'Simulation Name, Start Month and End Month must be entered' });
      return false;
    }

    let startYear = parseInt(params.startMonth.slice(0, 4));
    let endYear = parseInt(params.endMonth.slice(0, 4));
    let startMonth = parseInt(params.startMonth.slice(5));
    let endMonth = parseInt(params.endMonth.slice(5));

    if (startYear > endYear || (startYear === endYear && startMonth > endMonth)) {
      this.setState({ validated: false, errorText: 'Start Month must come before End Month' });
      return false;
    }

    if (!this.validateLoadMonths('loadCpData', params)) {
      this.setState({
        validated: false,
        errorText: `When reloading CP data, select a number of months to load between 1 and ${this.monthsCoverage}`,
      });
      return false;
    }

    if (!this.validateLoadMonths('loadFortuneData', params)) {
      this.setState({
        validated: false,
        errorText: `When reloading FRI data, select a number of months to load between 1 and ${this.monthsCoverage}`,
      });
      return false;
    }

    if (!this.validateLoadMonths('loadDucsiData', params)) {
      this.setState({
        validated: false,
        errorText: `When reloading Ducsi data, select a number of months to load between 1 and ${this.monthsCoverage}`,
      });
      return false;
    }

    if (!this.validateLoadMonths('loadLiquidationData', params)) {
      this.setState({
        validated: false,
        errorText: `When reloading Liquidation data, select a number of months to load between 1 and ${this.monthsCoverage}`,
      });
      return false;
    }

    if (!this.validateLoadMonths('loadSpotData', params)) {
      this.setState({
        validated: false,
        errorText: `When reloading Spot data, select a number of months to load between 1 and ${this.monthsCoverage}`,
      });
      return false;
    }

    if (!this.validateLoadMonths('loadVariableFcBenchmarkData', params)) {
      this.setState({
        validated: false,
        errorText: `When reloading Variable Fc Benchmark data, select number of months to load between 1 and ${this.monthsCoverage}`,
      });
      return false;
    }

    if (!this.validateTestRunMonths(params)) {
      this.setState({
        validated: false,
        errorText: `Validation Test Periods must be a value between 1 and 12.`,
      });
      return false;
    }

    this.setState({ validated: true, errorText: '' });
    return true;
  };

  validateLoadMonths = (type, params) => {
    let dataMonths = parseInt(params[type + 'Months']);
    if (params[type]) {
      return dataMonths && dataMonths > 0 && dataMonths <= this.monthsCoverage;
    } else {
      return 0 === dataMonths;
    }
  };

  validateTestRunMonths = (params) => {
    const months = parseInt(params.validationTestRunMonths);
    return !isNaN(months) && !(months < 1 || months > 12);
  };

  setName = (event) => {
    let params = deepClone(this.state.params);
    params['name'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setStartMonth = (event) => {
    let params = deepClone(this.state.params);
    params['startMonth'] = event.detail.selectedOption.value;
    this.setState({ params: params }, this.validate);
  };

  setEndMonth = (event) => {
    let params = deepClone(this.state.params);
    params['endMonth'] = event.detail.selectedOption.value;
    this.setState({ params: params }, this.validate);
  };

  toggleLoadData = (type, checked) => {
    let params = deepClone(this.state.params);
    params[type] = checked;
    params[type + 'Months'] = checked ? this.defaults[type] : '0';
    this.setState({ params: params }, this.validate);
  };

  setEndToEndSimulation = (event) => {
    this.toggleLoadData('endToEndSimulation', event.detail.checked);
  };

  setHcpSimulationOutputPath = (event) => {
    let params = deepClone(this.state.params);
    params['hcpSimulationOutputPath'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setLoadCpData = (event) => {
    this.toggleLoadData('loadCpData', event.detail.checked);
  };

  setLoadCpDataMonths = (event) => {
    let params = deepClone(this.state.params);
    params['loadCpDataMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setLoadFortuneData = (event) => {
    this.toggleLoadData('loadFortuneData', event.detail.checked);
  };

  setLoadFortuneDataMonths = (event) => {
    let params = deepClone(this.state.params);
    params['loadFortuneDataMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setLoadDucsiData = (event) => {
    this.toggleLoadData('loadDucsiData', event.detail.checked);
  };

  setLoadDucsiDataMonths = (event) => {
    let params = deepClone(this.state.params);
    params['loadDucsiDataMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setLoadLiquidationData = (event) => {
    this.toggleLoadData('loadLiquidationData', event.detail.checked);
  };

  setLoadLiquidationDataMonths = (event) => {
    let params = deepClone(this.state.params);
    params['loadLiquidationDataMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setLoadSpotData = (event) => {
    this.toggleLoadData('loadSpotData', event.detail.checked);
  };

  setLoadSpotDataMonths = (event) => {
    let params = deepClone(this.state.params);
    params['loadSpotDataMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setLoadVariableFcBenchmarkData = (event) => {
    this.toggleLoadData('loadVariableFcBenchmarkData', event.detail.checked);
  };

  setLoadVariableFcBenchmarkDataMonths = (event) => {
    let params = deepClone(this.state.params);
    params['loadVariableFcBenchmarkDataMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setUseStagedChanges = (event) => {
    let params = deepClone(this.state.params);
    logger.info(this.props.user);
    if (event.detail.checked) {
      params['stagingArea'] = this.props.user;
    } else {
      delete params['stagingArea'];
    }
    this.setState({ params: params }, this.validate);
  };

  setStagingArea = (event) => {
    let params = deepClone(this.state.params);
    if (event.detail.value) {
      params['stagingArea'] = event.detail.value;
    } else {
      delete params['stagingArea'];
    }
    this.setState({ params: params }, this.validate);
  };

  setUseBenchmarkAdjustmentStagedChanges = (event) => {
    let params = deepClone(this.state.params);
    if (event.detail.checked) {
      params['benchmarkAdjustmentStagingArea'] = this.props.user;
    } else {
      delete params['benchmarkAdjustmentStagingArea'];
    }
    this.setState({ params: params }, this.validate);
  };

  setBenchmarkAdjustmentStagingArea = (event) => {
    let params = deepClone(this.state.params);
    if (event.detail.value) {
      params['benchmarkAdjustmentStagingArea'] = event.detail.value;
    } else {
      delete params['benchmarkAdjustmentStagingArea'];
    }
    this.setState({ params: params }, this.validate);
  };

  setAutoBenchmarkAdjustment = (event) => {
    let params = deepClone(this.state.params);
    params['enableBenchmarkAdjustment'] = event.detail.checked;
    if (!event.detail.checked) {
      delete params['autoBenchmarkAdjustmentComponents'];
      params['autoBenchmarkAdjustmentSourceLoadMonths'] = '0';
    }
    this.setState({ params: params }, this.validate);
  };

  setAutoBenchmarkAdjustmentComponents = (event) => {
    let params = deepClone(this.state.params);
    params['autoBenchmarkAdjustmentComponents'] = event.detail.selectedOptions.map(o => o.value);
    this.setState({ params: params }, this.validate);
  };

  setAutoBenchmarkAdjustmentSourceLoadMonths = (event) => {
    let params = deepClone(this.state.params);
    params['autoBenchmarkAdjustmentSourceLoadMonths'] = event.detail.value;
    this.setState({ params: params }, this.validate);
  };

  setSimulationComponent = (event) => {
    let params = deepClone(this.state.params);
    params['components'] = [event.detail.selectedOption.id.toString()];
    this.setState({ params: params }, this.validate);
  };

  setSimulationProductLines = (event) => {
    const ops = event.detail.selectedOptions;
    if (ops === undefined || !ops.length) {
      this.productLineIds = [];
    } else {
      this.productLineIds = ops.map(o => o.value);
    }

    let params = deepClone(this.state.params);
    params['productLines'] = this.productLineIds;
    this.setState({ params: params }, this.validate);
  };

  setValidationTestRunMonths = (event) => {
    let params = deepClone(this.state.params);
    params['validationTestRunMonths'] = event.detail.selectedOption.id.toString();
    this.setState({ params: params }, this.validate);
  };

  toggleSkipValidationTests = (event) => {
    let params = deepClone(this.state.params);
    params['skipValidationTests'] = event.detail.checked;
    this.setState({ params: params }, this.validate);
  };

  setSyncRawData = (event) => {
    let params = deepClone(this.state.params);
    params['syncRawData'] = event.detail.checked;
    this.setState({ params: params }, this.validate);
  };

  setSyncAurora = (event) => {
    let params = deepClone(this.state.params);
    params['syncAurora'] = event.detail.checked;
    this.setState({ params: params }, this.validate);
  };

  setRunAccuracyPipeline = (event) => {
    let params = deepClone(this.state.params);
    params['runAccuracyPipeline'] = event.detail.checked;
    this.setState({ params: params }, this.validate);
  };

  setSimulationVisible = (event) => {
    let params = deepClone(this.state.params);
    params['publicView'] = event.detail.checked;
    this.setState({ params: params }, this.validate);
  }

  onSubmit = () => {
    if (this.validate()) {
      let params = deepClone(this.state.params);
      params['user'] = this.state.user;
      params['startMonth'] = parseInt(params['startMonth'].replace('/', ''));
      params['endMonth'] = parseInt(params['endMonth'].replace('/', ''));
      params['loadCpDataMonths'] = parseInt(params['loadCpDataMonths']);
      params['loadFortuneDataMonths'] = parseInt(params['loadFortuneDataMonths']);
      params['loadDucsiDataMonths'] = parseInt(params['loadDucsiDataMonths']);
      params['loadLiquidationDataMonths'] = parseInt(params['loadLiquidationDataMonths']);
      params['loadSpotDataMonths'] = parseInt(params['loadSpotDataMonths']);
      params['loadVariableFcBenchmarkDataMonths'] = parseInt(params['loadVariableFcBenchmarkDataMonths']);
      params['validationTestRunMonths'] = params['skipValidationTests']
        ? 0
        : parseInt(params['validationTestRunMonths']);
      let payload = { simulation: params };
      this.setState({ runningSimulation: true });
      this.client.runSimulation(this.onRunSimulationSuccess, this.onRunSimulationFailure, payload);
    }
  };

  onRunSimulationSuccess = (simulation) => {
    this.setState({ runningSimulation: false, params: this.defaultParams, validated: false, errorText: '' });
    this.props.onSubmit();
  };

  onRunSimulationFailure = (error) => {
    logger.error('Failure to run simulation', error);
    this.setState({
      runningSimulation: false,
      errorText: 'Simulation failed to start. This might be because your simulation name is already in use.',
    });
  };

  onCancel = () => {
    this.setState({ params: this.defaultParams });
    this.props.onCancel();
  };

  generateValidationTestRunMonthOptions = () => {
    return Array(12)
      .fill()
      .map((_, i) => {
        return { id: i + 1, label: i + 1 };
      });
  };

  render() {
    const { params } = this.state;

    const validationTestResultOptions = this.generateValidationTestRunMonthOptions();
    const selectedOption = validationTestResultOptions.find(
      (item) => item.id.toString() === params.validationTestRunMonths
    );

    return (
      <Modal
        visible={true}
        size="large"
        header="New Simulation"
        expandToFit={true}
        onDismiss={this.onCancel}
        footer={
          <div>
            {this.state.errorText && (
              <span className="awsui-util-status-negative">
                <Icon name="status-negative" /> {this.state.errorText}
              </span>
            )}
            <span className="awsui-util-f-r">
              <Button text="Cancel" onClick={this.onCancel} />
              <Button
                variant="primary"
                text="Run Simulation"
                loading={this.state.runningSimulation}
                onClick={this.onSubmit}
                disabled={!this.state.validated}
              />
            </span>
          </div>
        }
        content={
          <div>
            <Input className={'awsui-util-mb-s'} placeholder="Simulation Name" onChange={this.setName} />
            <FormField label={'Data Range'} description={'Months to be available in snapshot after simulation'}>
              <ColumnLayout className={'awsui-util-mb-xxl'} columns={2}>
                <div data-awsui-column-layout-root="true">
                  <TimeFrameButton
                    startMonth={DATE_YYYYMM.START_MONTH}
                    endMonth={DATE_YYYYMM.PREV_MONTH}
                    onChange={this.setStartMonth}
                    selected={this.state.params.startMonth}
                    multiSelect={false}
                    reverse={true}
                  />
                  <TimeFrameButton
                    startMonth={DATE_YYYYMM.START_MONTH}
                    endMonth={DATE_YYYYMM.PREV_MONTH}
                    onChange={this.setEndMonth}
                    selected={this.state.params.endMonth}
                    multiSelect={false}
                  />
                </div>
              </ColumnLayout>
            </FormField>

            <div className={'awsui-util-mb-xxl'}>
              <FormField
                label="Validation Test Periods"
                description={'No of months from end date for which validation tests are to be run.'}
              >
                <ColumnLayout className={'awsui-util-mb-xxl'} columns={2}>
                  <div data-awsui-column-layout-root="true">
                    <Select
                      options={validationTestResultOptions}
                      selectedOption={selectedOption}
                      onChange={this.setValidationTestRunMonths}
                      disabled={params.skipValidationTests}
                    />
                    <Toggle onChange={this.toggleSkipValidationTests} checked={params.skipValidationTests}>
                      Skip validation
                    </Toggle>
                  </div>
                </ColumnLayout>
              </FormField>
            </div>

            <div className={'awsui-util-mb-xxl'}>
              <Toggle onChange={this.setSimulationVisible} checked={params.publicView}>
                Public
              </Toggle>
            </div>

            <div className={'awsui-util-mb-xxl'}>
              <Toggle onChange={this.setUseStagedChanges} checked={'stagingArea' in params}>
                Apply Staged Mapping Changes
              </Toggle>
              {'stagingArea' in params && isAdmin(this.props.identity) && (
                <Box margin={{ left: "xl" }}>
                  <FormField description="Staging Area">
                    <Input onChange={this.setStagingArea} value={params.stagingArea} />
                  </FormField>
                </Box>
              )}
              <Toggle onChange={this.setUseBenchmarkAdjustmentStagedChanges}
                      checked={'benchmarkAdjustmentStagingArea' in params}>
                Apply Benchmark Adjustment Draft
              </Toggle>
              {'benchmarkAdjustmentStagingArea' in params && isAdmin(this.props.identity) && (
                <Box margin={{ left: "xl" }}>
                  <FormField description="Staging Area">
                    <Input onChange={this.setBenchmarkAdjustmentStagingArea}
                           value={params.benchmarkAdjustmentStagingArea} />
                  </FormField>
                </Box>
              )}
            </div>

            {isAdmin(this.props.identity) && (
              <div>
                <div className={'awsui-util-mb-s'}>
                  <Toggle onChange={this.setEndToEndSimulation} checked={params.endToEndSimulation}>
                    End To End Simulation
                  </Toggle>
                  {params.endToEndSimulation && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="CP Simulation Output Path">
                        <Input onChange={this.setHcpSimulationOutputPath} value={params.hcpSimulationOutputPath} />
                      </FormField>
                      <FormField description="Component">
                        <Select
                        options={this.state.componentOptions}
                        onChange={this.setSimulationComponent}
                        />
                      </FormField>

                      <FormField description="Product Lines">
                        <div className={NegativeMarginAccuracyButtonNewStyle}>
                        <ProductLinesButton
                          placeholder={"Product Lines"}
                          productLines={this.state.productLinesOptions}
                          onChange={this.setSimulationProductLines}
                          multiselect={true}
                          disabled={false}
                          required={false}
                          selected={this.productLineIds}
                          productLineParentFilter={[]}
                        />
                        </div>
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-s'}>
                  <Toggle onChange={this.setLoadCpData} checked={params.loadCpData}>
                    Reload CP Data
                  </Toggle>
                  {params.loadCpData && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="CP Data Months to Reload">
                        <Input onChange={this.setLoadCpDataMonths} value={params.loadCpDataMonths} />
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-s'}>
                  <Toggle onChange={this.setLoadFortuneData} checked={params.loadFortuneData}>
                    Reload FRI data
                  </Toggle>
                  {params.loadFortuneData && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="FRI Data Months to Reload">
                        <Input onChange={this.setLoadFortuneDataMonths} value={params.loadFortuneDataMonths} />
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-s'}>
                  <Toggle onChange={this.setLoadDucsiData} checked={params.loadDucsiData}>
                    Reload DUCSI Data
                  </Toggle>
                  {params.loadDucsiData && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="Ducsi Data Months to Reload">
                        <Input onChange={this.setLoadDucsiDataMonths} value={params.loadDucsiDataMonths} />
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-s'}>
                  <Toggle onChange={this.setLoadLiquidationData} checked={params.loadLiquidationData}>
                    Reload Liquidation Data
                  </Toggle>
                  {params.loadLiquidationData && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="Liquidation Data Months to Reload">
                        <Input onChange={this.setLoadLiquidationDataMonths} value={params.loadLiquidationDataMonths} />
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-s'}>
                  <Toggle onChange={this.setLoadSpotData} checked={params.loadSpotData}>
                    Reload Spot Data
                  </Toggle>
                  {params.loadSpotData && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="Spot Data Months to Reload">
                        <Input onChange={this.setLoadSpotDataMonths} value={params.loadSpotDataMonths} />
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-xxl'}>
                  <Toggle onChange={this.setLoadVariableFcBenchmarkData} checked={params.loadVariableFcBenchmarkData}>
                    Reload Variable Fc Benchmark Data
                  </Toggle>
                  {params.loadVariableFcBenchmarkData && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="Variable Fc Benchmark Data Months to Reload">
                        <Input onChange={this.setLoadVariableFcBenchmarkDataMonths} value={params.loadVariableFcBenchmarkDataMonths} />
                      </FormField>
                    </Box>
                  )}
                </div>

                <div className={'awsui-util-mb-xxl'} style={{ display: 'none' }}>
                  <Toggle onChange={this.setAutoBenchmarkAdjustment}
                          checked={params.enableBenchmarkAdjustment}
                          disabled={true}>
                    Automate Benchmark Adjustment
                  </Toggle>
                  {params.enableBenchmarkAdjustment && (
                    <Box margin={{ left: "xl" }}>
                      <FormField description="Months of benchmark adjustment source data to load (0 = use existing data)">
                        <Input onChange={this.setAutoBenchmarkAdjustmentSourceLoadMonths}
                               value={params.autoBenchmarkAdjustmentSourceLoadMonths}
                               type="number" />
                      </FormField>
                      <FormField description="Launched components are always included. Select additional testing components below.">
                        <Multiselect
                          selectedOptions={BENCHMARK_ADJUSTMENT_COMPONENTS.filter(
                            o => (params.autoBenchmarkAdjustmentComponents || []).includes(o.value))}
                          onChange={this.setAutoBenchmarkAdjustmentComponents}
                          options={BENCHMARK_ADJUSTMENT_COMPONENTS}
                          placeholder="Add testing components (optional)..."
                        />
                      </FormField>
                    </Box>
                  )}
                </div>

                <Toggle className={'awsui-util-mb-s'} checked={params.syncRawData} onChange={this.setSyncRawData}>
                  Sync Raw Data
                </Toggle>
                <Toggle className={'awsui-util-mb-s'} checked={params.syncAurora} onChange={this.setSyncAurora}>
                  Sync Aurora
                </Toggle>
                <Toggle
                  className={'awsui-util-mb-s'}
                  checked={params.runAccuracyPipeline}
                  onChange={this.setRunAccuracyPipeline}
                >
                  Run Accuracy Pipeline
                </Toggle>
              </div>
            )}
          </div>
        }
      />
    );
  }
}

export default NewSimulationWindow;



// WEBPACK FOOTER //
// ./src/components/simulator/new_simulation_window.jsx